import type { ReactNode } from 'react';
import styles from './Chip.module.css';

/**
 * **`Chip`**（`docs/design-system.md` §5.1 / §5.7）。
 *
 * ## 型で守っていること
 *
 * **押させない**（§5.7: static のみ）。**`onClick` を受け取らない** —
 * 「押せる一覧行は**行の側**が受ける」という規約を、型で執行する。
 *
 * **`role` は必ずラベルを併記する**（§2.6 規約3: 役割を色だけで伝えない）。
 * `children` を必須にすることで**色だけのチップを作れなくしている**。
 */
export type ChipProps =
  | {
      readonly variant: 'role';
      readonly role: 'learner' | 'ek' | 'coach' | 'viewer';
      readonly children: ReactNode;
    }
  | {
      readonly variant: 'status';
      readonly status: 'ok' | 'warn' | 'danger' | 'neutral';
      readonly children: ReactNode;
    }
  | {
      /** **無彩色 + 点の塗り。状態色を使わない**（§8.1） */
      readonly variant: 'lifecycle';
      readonly filled: boolean;
      readonly children: ReactNode;
    }
  | {
      /** `conceptKey` の列。**順位を作らない**（D98） */
      readonly variant: 'concept';
      readonly children: ReactNode;
    };

export function Chip(props: ChipProps): ReactNode {
  const cls = (...names: (string | undefined)[]): string =>
    [styles['chip'], ...names].filter(Boolean).join(' ');

  if (props.variant === 'role') {
    return <span className={cls(styles[props.role])}>{props.children}</span>;
  }
  if (props.variant === 'status') {
    return <span className={cls(styles[props.status])}>{props.children}</span>;
  }
  if (props.variant === 'lifecycle') {
    return (
      <span className={cls(styles['lifecycle'])}>
        <span
          className={[styles['dot'], props.filled ? undefined : styles['dotHollow']]
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        />
        {props.children}
      </span>
    );
  }
  return <span className={cls(styles['concept'])}>{props.children}</span>;
}
